// @flow

import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { toASCII, toUnicode } from 'punycode.js';

import { selectIsAuthenticatedUser } from '@gandi/authentication';
import {
  callDomainCatalogFail,
  callDomainCatalogStart,
  callDomainCatalogSuccess,
  commitCart,
  fetchClaimsFail,
  fetchClaimsSuccess,
  getCart,
  removeDiscount,
  selectCartCurrency,
  selectCartGrid,
  selectDiscounts,
  selectProducts,
  type DomainProduct,
} from '@gandi/cart';
import type { Fqdn } from '@gandi/domain-configuration';
import { selectFromConfig } from '@gandi/redux-config';
import { selectCurrentId } from '@gandi/redux-sharing';
import type { FetchResponse } from '@gandi/resource/redux';
import { SSEPollerClient } from '@gandi/sse-poller/client';
import { getMiddlewareEndpointUrl } from '@gandi/sse-poller/helpers';

import { setAppWorking, unsetAppWorking } from '../../modules/app';
import type { Dispatch, ThunkAction } from '../../types';

type FqdnIdMapping = {
  [fqdn: Fqdn]: string,
};

/**
 * Declare listeners passed to the SSE Poller client.
 */
const sseListeners = (
  dispatch: Dispatch,
  fqdnIdMapping: FqdnIdMapping,
): ({
  error: (err: any) => void,
  giveupJob: (response: any) => void,
  response: (response: any) => void,
}) => ({
  // if the job has not succeeded after some attempts
  giveupJob: response => {
    const fqdn = get(response, 'data.identifier');
    const productId = fqdnIdMapping[fqdn];

    if (productId) {
      dispatch(callDomainCatalogFail(productId));
    }
  },
  // if the job has triggered an error
  error: err => {
    const fqdn = get(err, 'data.products.0.name', '');
    const productId = fqdnIdMapping[fqdn];

    if (productId) {
      dispatch(callDomainCatalogFail(productId));
    }
  },
  // if the catalog has succeeded
  response: response => {
    const catalog = get(response, 'data.products.0.prices', []);
    const fqdn = get(response, 'data.products.0.name', '');
    const productId = fqdnIdMapping[fqdn];

    if (productId && !isEmpty(catalog)) {
      // set catalog data into the product
      dispatch(
        callDomainCatalogSuccess({
          productId,
          apiResults: catalog,
        }),
      );
    }
  },
});

/**
 * Create a mapping between Fqdn and product uuid.
 */
const getFqdnIdMapping = (
  domainProducts: Array<DomainProduct>,
): FqdnIdMapping =>
  domainProducts.reduce((acc, product) => {
    const fqdn = product.fqdn;
    const productId = product.id.toString();

    if (!fqdn) return acc;

    acc[fqdn] = productId;

    try {
      // Domain creation products return an ASCII fqdn
      // while domain renewal products return an unicode fqdn.
      // Allow mapping to both formats if they are different.
      const decoded = toUnicode(fqdn || '');
      const encoded = toASCII(fqdn || '');

      acc[decoded] = productId;
      acc[encoded] = productId;
    } catch (e) {
      // keep only fqdn key
    }

    return acc;
  }, ({}: FqdnIdMapping));

/**
 * Get catalog for a list of products from a SSE middleware.
 * When receiving a response, update the product.
 */

export const fetchDomainCreateProductsCatalog =
  (domainCreateProducts: Array<DomainProduct>): ThunkAction<SSEPollerClient> =>
  (dispatch, getState) => {
    // set products as working
    domainCreateProducts.forEach(product => {
      dispatch(callDomainCatalogStart(product.id));
    });

    const state = getState();

    const middlewareUrl = getMiddlewareEndpointUrl(
      selectFromConfig<string>('public_url')(state),
      selectFromConfig<string>('app.endpoint')(state),
      'catalog',
    );

    // Retrieve `lang` option value for domains requiring it
    // to be passed along other params to the catalog route of Prices API.
    const langOptions = domainCreateProducts.reduce((acc, product) => {
      const { lang } = product.payload;

      if (lang && product.fqdn) return { ...acc, [product.fqdn]: lang };

      return acc;
    }, {});

    const fqdns = domainCreateProducts.map(product => product.fqdn);
    // Some domain products (IDNs) have a `lang` attribute in the product payload,
    // this param *must* be passed to the Catalog route of Prices API.
    const langs = domainCreateProducts.map(product =>
      // $FlowFixMe[invalid-computed-prop]
      product.fqdn ? langOptions[product.fqdn] || '' : '',
    );

    let params = {
      name: fqdns,
      lang: langs,
      processes: 'create',
      currency: selectCartCurrency(state),
      grid: selectCartGrid(state),
    };

    const sharingId = selectCurrentId(state);
    if (sharingId) {
      params = {
        ...params,
        sharing_id: sharingId,
      };
    }

    // if not authenticated, pass the 'not_authenticated' param in the query string
    // to bypass the login middleware
    if (!selectIsAuthenticatedUser(state)) {
      params = {
        ...params,
        not_authenticated: 'true',
      };
    }

    const fqdnIdMapping = getFqdnIdMapping(domainCreateProducts);
    const listeners = sseListeners(dispatch, fqdnIdMapping);

    return new SSEPollerClient(middlewareUrl, params, listeners);
  };

export const fetchDomainRenewProductsCatalog =
  (domainRenewProducts: Array<DomainProduct>): ThunkAction<SSEPollerClient> =>
  (dispatch, getState): SSEPollerClient => {
    // set products as working
    domainRenewProducts.forEach(product => {
      dispatch(callDomainCatalogStart(product.id));
    });

    const state = getState();

    const middlewareUrl = getMiddlewareEndpointUrl(
      selectFromConfig<string>('public_url')(state),
      selectFromConfig<string>('app.endpoint')(state),
      'catalog',
    );
    const fqdns = domainRenewProducts.map(product => product.identifier);

    let params = {
      name: fqdns,
      processes: 'renew',
      currency: selectCartCurrency(state),
    };

    const sharingId = selectCurrentId(state);
    if (sharingId) {
      params = {
        ...params,
        sharing_id: sharingId,
      };
    }

    const fqdnIdMapping = getFqdnIdMapping(domainRenewProducts);
    const listeners = sseListeners(dispatch, fqdnIdMapping);

    return new SSEPollerClient(middlewareUrl, params, listeners);
  };

/**
 * Get claims for a list of products from a SSE middleware.
 * When receiving a response, update the product.
 */
export const fetchClaims =
  (domainCreateProducts: Array<DomainProduct>): ThunkAction<SSEPollerClient> =>
  (dispatch, getState) => {
    const fqdnIdMapping = getFqdnIdMapping(domainCreateProducts);

    const listeners = {
      response: (response: FetchResponse<any, { fqdn: Fqdn }>) => {
        const fqdn = response?.data?.fqdn;
        const productId = fqdnIdMapping[fqdn];

        if (productId) {
          dispatch(
            fetchClaimsSuccess({
              productId,
              claims: response?.data,
            }),
          );
        }
      },
      error: () => {
        dispatch(fetchClaimsFail());
      },
    };

    const state = getState();

    const middlewareUrl = getMiddlewareEndpointUrl(
      selectFromConfig<string>('public_url')(state),
      selectFromConfig<string>('app.endpoint')(state),
      'claims',
    );

    const params = {
      fqdn: domainCreateProducts.map(product => product.label),
    };

    // $FlowFixMe[incompatible-call]
    // $FlowFixMe[incompatible-exact]
    // $FlowFixMe[incompatible-indexer]
    // $FlowFixMe[prop-missing]
    return new SSEPollerClient(middlewareUrl, params, listeners);
  };

const removeUselessDiscounts =
  (): ThunkAction<
    Promise<Array<$SettledPromiseResult<Awaited<Promise<mixed>>>>>,
  > =>
  (dispatch, getState) => {
    const appliedDiscounts = selectDiscounts(getState());
    const products = selectProducts(getState());
    // iterate over discounts to remove in order to limit edge cases.

    const actions = appliedDiscounts.reduce(
      (promises, discount) => {
        if (discount.code && /^GWH68/.test(discount.code)) {
          // we should remove the discount if there is no shs product
          // that is not a trial.
          const shouldRemoveDiscount =
            !products.length ||
            !products.filter(product => {
              // non `paas` products cannot use discount
              if (product.productType !== 'paas') return false;
              // only non trial `create` can have a discount applied
              if (product.process === 'create') return !product.payload.trial;
              // discount can be used on any simple hosting renew.
              if (product.process === 'renew') return true;
              // any other product cannot use this discount
              return false;
            }).length;

          if (shouldRemoveDiscount) {
            return [dispatch(removeDiscount(String(discount.code)))].concat(
              promises,
            );
          }
        }

        return promises;
      },
      ([]: Array<Promise<mixed>>),
    );

    return Promise.allSettled(actions);
  };

export type DoCommitCartThunkAction = (body?: {
  external_reference?: string,
}) => ThunkAction<Promise<any>>;
export type DoCommitCartBoundAction = () => Promise<any>;
export const doCommitCart: DoCommitCartThunkAction =
  body => async (dispatch: Dispatch) => {
    dispatch(setAppWorking());

    let response;
    try {
      await dispatch(removeUselessDiscounts());
      response = await dispatch(commitCart({}, body));
      await dispatch(getCart());
    } finally {
      dispatch(unsetAppWorking());
    }
    return response;
  };
